/**
 * Gibbs Action Registry
 *
 * Central registry of all drug discovery actions. Imports all category
 * modules and provides lookup APIs for workflow composition.
 *
 * @module gibbs/registry/registry
 */

import { isTypeCompatible } from './types';
import { validateActionDef } from './schema';
import { evaluatePrecondition } from './preconditions';
import { warn } from '../logger';

// Import all action category modules
import targetSetupActions from './actions/target-setup';
import compoundMgmtActions from './actions/compound-mgmt';
import fragmentOpsActions from './actions/fragment-ops';
import simulationActions from './actions/simulation';
import distributedJobsActions from './actions/distributed-jobs';
import propertyPredActions from './actions/property-pred';
import searchFilterActions from './actions/search-filter';
import analysisActions from './actions/analysis';
import exportActions from './actions/export';
import workspaceActions from './actions/workspace';
import externalDataActions from './actions/external-data';
import plannedAim1Actions from './actions/planned-aim1';
import plannedAim2Actions from './actions/planned-aim2';
import medchemDesignActions from './actions/medchem-design';
import displayActions from './actions/display';
import uiOperationsActions from './actions/ui-operations';
import uiPanelOpenersActions from './actions/ui-panel-openers';
import displayTreeOpsActions from './actions/display-tree-ops';
import fragmentLibrarySetupActions from './actions/fragment-library-setup';
import connectedDataAcquisitionActions from './actions/connected-data-acquisition';
import categoryBExtrasActions from './actions/category-b-extras';
import destructiveOpsActions from './actions/destructive-ops';

// ─── Build Registry ──────────────────────────────────────────────────────────

/** @type {Map<string, import('./schema.js').ActionDef>} */
const actionMap = new Map();

const allActionArrays = [
    targetSetupActions,
    compoundMgmtActions,
    fragmentOpsActions,
    simulationActions,
    distributedJobsActions,
    propertyPredActions,
    searchFilterActions,
    analysisActions,
    exportActions,
    workspaceActions,
    externalDataActions,
    plannedAim1Actions,
    plannedAim2Actions,
    medchemDesignActions,
    displayActions,
    uiOperationsActions,
    uiPanelOpenersActions,
    displayTreeOpsActions,
    fragmentLibrarySetupActions,
    connectedDataAcquisitionActions,
    categoryBExtrasActions,
    destructiveOpsActions,
];

for (const actions of allActionArrays) {
    for (const action of actions) {
        if (actionMap.has(action.id)) {
            warn(`Duplicate action id: "${action.id}" — skipping`);
            continue;
        }
        const errors = validateActionDef(action);
        if (errors.length > 0) {
            warn(`Invalid action "${action.id}": ${errors.join('; ')}`);
        }
        actionMap.set(action.id, action);
    }
}

// ─── Core Lookup APIs ────────────────────────────────────────────────────────

/**
 * Get an action definition by its unique ID.
 *
 * @param {string} id - Kebab-case action identifier
 * @returns {import('./schema.js').ActionDef | undefined}
 */
export function getAction(id) {
    return actionMap.get(id);
}

/**
 * Get all action definitions in a given category.
 *
 * @param {string} category - Category name (e.g., "Target Setup")
 * @returns {import('./schema.js').ActionDef[]}
 */
export function getActionsByCategory(category) {
    return Array.from(actionMap.values()).filter((a) => a.category === category);
}

/**
 * Get all registered action definitions.
 *
 * @returns {import('./schema.js').ActionDef[]}
 */
export function getAllActions() {
    return Array.from(actionMap.values());
}

/**
 * Get the total count of registered actions.
 *
 * @returns {number}
 */
export function getActionCount() {
    return actionMap.size;
}

// ─── Workflow Composition APIs ───────────────────────────────────────────────

/**
 * Find actions that accept a given type as one of their inputs.
 * Uses isTypeCompatible() to match, so a Compound output will match
 * actions that accept CompoundList.
 *
 * @param {string} type - Workflow type name
 * @returns {import('./schema.js').ActionDef[]}
 */
export function getActionsAcceptingInput(type) {
    return Array.from(actionMap.values()).filter(
        (action) => action.inputs.some((input) => isTypeCompatible(type, input.type))
    );
}

/**
 * Find actions that produce a given type as one of their outputs.
 * Uses isTypeCompatible() to match.
 *
 * @param {string} type - Workflow type name
 * @returns {import('./schema.js').ActionDef[]}
 */
export function getActionsProducingOutput(type) {
    return Array.from(actionMap.values()).filter(
        (action) => action.outputs.some((output) => isTypeCompatible(output.type, type))
    );
}

/**
 * Given an action, find all actions that could come next in a workflow.
 * An action B can follow action A if any of A's output types are
 * compatible with any of B's input types.
 *
 * @param {string} actionId - The upstream action ID
 * @returns {import('./schema.js').ActionDef[]}
 */
export function getCompatibleNextActions(actionId) {
    const action = actionMap.get(actionId);
    if (!action) return [];

    const outputTypes = action.outputs.map((o) => o.type);
    if (outputTypes.length === 0) return [];

    return Array.from(actionMap.values()).filter((candidate) => {
        if (candidate.id === actionId) return false;
        return candidate.inputs.some(
            (input) => outputTypes.some((outType) => isTypeCompatible(outType, input.type))
        );
    });
}

// ─── Precondition Checking ───────────────────────────────────────────────────

/**
 * Check all preconditions for an action against the current workspace state.
 *
 * @param {string} actionId - Action to check
 * @param {Object} workspaceState - Workspace state object
 * @returns {{ met: boolean, unmet: string[] }}
 */
export function checkPreconditions(actionId, workspaceState) {
    const action = actionMap.get(actionId);
    if (!action) return { met: false, unmet: [`Unknown action: ${actionId}`] };

    const unmet = [];
    for (const precondition of action.preconditions) {
        try {
            if (!evaluatePrecondition(precondition.check, workspaceState)) {
                unmet.push(precondition.check);
            }
        } catch (e) {
            unmet.push(`${precondition.check} (error: ${e.message})`);
        }
    }

    return { met: unmet.length === 0, unmet };
}
